/**
 * Commands for MLeader leader line operations with undo/redo support.
 *
 * AddLeaderLineCommand: Adds a leader line to an existing MLeader
 * RemoveLeaderLineCommand: Removes a leader line from an MLeader
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, findEntityByHandle } from "../core/oda";
import { OpenMode } from "../core/constants";
import { addLeaderLineToMLeader, type Point3D } from "../leaders/conversion";
import {
  getEntityLayerName,
  recordGraphicsModified,
  setEntityLayer,
} from "../entities";
import { selectionService } from "../selection";
import { UNDO_LAYER_NAME } from "../database";

/**
 * Parameters for AddLeaderLineCommand.
 */
export interface AddLeaderLineParams {
  /** Handle of the MLeader entity */
  mleaderHandle: string;
  /** Arrow tip position */
  arrowTip: Point3D;
}

/**
 * Command to add a leader line to an existing MLeader.
 */
export class AddLeaderLineCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: AddLeaderLineParams;
  private executed = false;

  constructor(viewer: DRDrawingViewer, params: AddLeaderLineParams) {
    this.viewer = viewer;
    this.params = params;
  }

  execute(): void {
    if (this.executed) return;

    const db = this.viewer.database;
    if (!db) return;

    // Use resolveCurrentHandle in case the MLeader was recreated
    //const currentHandle = resolveCurrentHandle(this.params.mleaderHandle);
    const currentHandle = this.params.mleaderHandle;
    const mleaderId = findEntityByHandle(this.viewer.module, db, currentHandle);
    if (!mleaderId) {
      console.error("[AddLeaderLineCommand] MLeader not found");
      return;
    }

    // Add the leader line
    const success = addLeaderLineToMLeader(
      this.viewer,
      mleaderId,
      this.params.arrowTip,
    );
    if (success) {
      recordGraphicsModified(this.viewer.module, mleaderId);
    }

    this.executed = true;
    this.viewer.update();
  }

  undo(): void {
    if (!this.executed) return;
    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;

    const currentHandle = this.params.mleaderHandle;
    const mleaderId = findEntityByHandle(module, db, currentHandle);
    if (!mleaderId) return;
    // Find and remove the leader line we added
    // Since we stored the arrow tip, we can find the line by its first vertex
    const mleader = openAs(
      mleaderId,
      this.viewer.module.OdDbMLeader,
      OpenMode.kForWrite,
    );
    if (!mleader) return;
    const lineIndex = this.findLeaderLineByArrowTip(
      mleader,
      this.params.arrowTip,
    );
    if (lineIndex !== null) {
      mleader.removeLeaderLine(lineIndex);
      recordGraphicsModified(module, mleaderId);
    }
    deleteObj(mleader);
    deleteObj(mleaderId);
    this.executed = false;
    this.viewer.update();
  }

  redo(): void {
    this.executed = false;
    this.execute();
  }

  dispose(): void {
    // Nothing to clean up
  }

  // FIXME : I did not manage to find a way to identify LeaderLine and I did this as a sketchy workaround
  private findLeaderLineByArrowTip(
    mleader: DrawingModule.OdDbMLeader,
    arrowTip: Point3D,
  ): number | null {
    const module = this.viewer.module;
    const tolerance = 0.001;

    // Get number of leader lines and try each potential index
    const numLines = mleader.numLeaderLines();
    const maxIndexToTry = numLines + 10; // Try extra indexes in case of gaps

    let foundCount = 0;
    for (
      let lineIndex = 0;
      lineIndex < maxIndexToTry && foundCount < numLines;
      lineIndex++
    ) {
      const point = new module.OdGePoint3d(0, 0, 0);
      const result = mleader.getFirstVertex(lineIndex, point);

      if (result === 0) {
        foundCount++;
        const dx = Math.abs(point.x - arrowTip.x);
        const dy = Math.abs(point.y - arrowTip.y);
        deleteObj(point);

        if (dx < tolerance && dy < tolerance) {
          return lineIndex;
        }
      } else {
        deleteObj(point);
      }
    }

    return null;
  }
}

/**
 * Parameters for RemoveLeaderLineCommand.
 */
export interface RemoveLeaderLineParams {
  /** Handle of the MLeader entity */
  mleaderHandle: string;
  /** Index of the leader line to remove */
  leaderLineIndex: number;
  /** Stored arrow tip position (for undo - re-adding the line) */
  arrowTip: Point3D;
}

/**
 * Command to remove a leader line from an MLeader.
 */
export class RemoveLeaderLineCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: RemoveLeaderLineParams;
  private executed = false;
  private originalLayerName = "";
  /** Handle of MText created when removing the last leader line */
  private createdMTextHandle: string | null = null;

  /** Whether this removal erased the MLeader (last line case) */
  wasLastLine = false;

  constructor(viewer: DRDrawingViewer, params: RemoveLeaderLineParams) {
    this.viewer = viewer;
    this.params = params;
  }

  /**
   * Get the handle of the MText created when removing the last leader line.
   * Returns null if the MLeader still exists (not the last line).
   */
  getCreatedMTextHandle(): string | null {
    return this.createdMTextHandle;
  }

  execute(): void {
    if (this.executed) {
      return;
    }

    const db = this.viewer.database;
    const module = this.viewer.module;
    if (!db) {
      console.error("[RemoveLeaderLineCommand] No database");
      return;
    }

    // Use resolveCurrentHandle in case the MLeader was recreated
    const currentHandle = this.params.mleaderHandle;
    const mleaderId = findEntityByHandle(this.viewer.module, db, currentHandle);
    if (!mleaderId) {
      console.error(
        "[RemoveLeaderLineCommand] MLeader not found by handle:",
        currentHandle,
      );
      return;
    }

    this.originalLayerName = getEntityLayerName(module, mleaderId);

    const mleader = openAs(
      mleaderId,
      this.viewer.module.OdDbMLeader,
      OpenMode.kForWrite,
    );

    // Check how many leader lines remain
    const numLines = mleader.numLeaderLines();

    if (numLines <= 1) {
      // Last leader line - send mleader to undo layer

      setEntityLayer(module, db, mleader, UNDO_LAYER_NAME);
      deleteObj(mleader);
      recordGraphicsModified(this.viewer.module, mleaderId);
      if (selectionService.has(mleaderId)) {
        selectionService.remove(mleaderId);
      }
      this.wasLastLine = true;
    } else {
      // Remove just the leader line
      mleader.removeLeaderLine(this.params.leaderLineIndex);
      deleteObj(mleader);
      recordGraphicsModified(this.viewer.module, mleaderId);
    }

    deleteObj(mleaderId);
    this.executed = true;
    this.viewer.update();
  }

  undo(): void {
    if (!this.executed) return;

    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;

    try {
      if (this.wasLastLine) {
        // Last leader was removed: MLeader was erased,
        const mleaderHandle = this.params.mleaderHandle;
        const mleaderId = findEntityByHandle(module, db, mleaderHandle);
        if (mleaderId) {
          this.setEntityLayer(mleaderId, this.originalLayerName);
        }
      } else {
        // Just a leader line was removed, re-add it
        // Use resolveCurrentHandle in case the MLeader was recreated by another undo
        const currentHandle = this.params.mleaderHandle;
        const mleaderId = findEntityByHandle(module, db, currentHandle);
        if (mleaderId) {
          addLeaderLineToMLeader(this.viewer, mleaderId, this.params.arrowTip);
          recordGraphicsModified(module, mleaderId);
        }
      }
    } catch (error) {
      console.error("[RemoveLeaderLineCommand.undo] Error:", error);
    }

    this.executed = false;
    this.viewer.update();
  }

  redo(): void {
    // For both cases (wasLastLine or not), we can just re-execute
    // In the wasLastLine case, undo updated this.params.mleaderHandle to the new MLeader
    this.executed = false;
    this.execute();
  }

  dispose(): void {
    // if command is not exectued, meaning that it was undone, nothing to clear
    if (!this.executed) return;
    const db = this.viewer.database;
    if (!db) return;
    if (this.executed && this.wasLastLine) {
      const entityId = findEntityByHandle(
        this.viewer.module,
        db,
        this.params.mleaderHandle,
      );
      if (entityId) {
        const entity = openAs(
          entityId,
          this.viewer.module.OdDbEntity,
          OpenMode.kForWrite,
        );
        const layerStr = entity.layer();
        const currentLayer = layerStr.toString();
        deleteObj(layerStr);

        if (currentLayer === UNDO_LAYER_NAME) {
          entity.erase(true);
        }
        deleteObj(entity);
      }
    }
  }

  private setEntityLayer(
    entityId: DrawingModule.OdDbObjectId,
    layerName: string,
  ): void {
    const entity = openAs(
      entityId,
      this.viewer.module.OdDbEntity,
      OpenMode.kForWrite,
    );
    const module = this.viewer.module;
    const layerStr = new module.OdString();
    layerStr.set(layerName);
    entity.setLayer(layerStr, false, true);
    deleteObj(layerStr);
    deleteObj(entity);
  }
}
